import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

import { s3HealthBadge, StatusBadge } from "@/shared/components/StatusBadge";
import { S3BucketIntegrationRecord } from "@/shared/domain/integrations/S3BucketIntegrationRecord";

import styles from "./HealthCheckDialog.module.css";
import { formatHealthToken, PROBE_ORDER } from "./s3HealthStatus";

interface HealthCheckDialogProps {
  open: boolean;
  onClose: () => void;
  bucket: S3BucketIntegrationRecord | undefined;
  onRunCheck: () => void;
  isRunning: boolean;
  runError: Error | null;
}

export function HealthCheckDialog({
  open,
  onClose,
  bucket,
  onRunCheck,
  isRunning,
  runError,
}: HealthCheckDialogProps) {
  if (bucket === undefined) {
    return null;
  }

  const summary = bucket.last_health_check;
  const probes = summary?.probes;
  const orderedProbes = probes
    ? PROBE_ORDER.filter((name) => name in probes)
    : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm">
      <DialogTitle>Health check — {bucket.bucket_name}</DialogTitle>
      <DialogContent>
        <div className={styles.content}>
          <div className={styles.statusRow}>
            <span className={styles.label}>Status</span>
            <StatusBadge status={s3HealthBadge(bucket.status)} />
          </div>

          {summary ? (
            <>
              <div className={styles.message}>{summary.message}</div>
              {probes && orderedProbes.length > 0 && (
                <div>
                  <div className={styles.probesTitle}>Checks</div>
                  {orderedProbes.map((name) => (
                    <div key={name} className={styles.probeRow}>
                      <span>{formatHealthToken(name)}</span>
                      <span className={styles.probeOutcome}>
                        {formatHealthToken(probes[name])}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className={styles.notChecked}>
              This bucket hasn&apos;t been checked yet. Run a check to verify
              Roboto can reach it.
            </div>
          )}

          {runError && (
            <div className={styles.errorText}>
              Health check could not be run: {runError.message}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isRunning}>
          Close
        </Button>
        <Button
          onClick={onRunCheck}
          disabled={isRunning}
          variant="contained"
          startIcon={
            isRunning ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {isRunning ? "Running…" : "Run health check"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
